import store from '@/store'
import kkmServerApiClient from '@/services/kkm-server/kkmServerApiClient'
import {
  STATUS,
  PROBE_INTERVAL_MS,
  PROBE_TIMEOUT_MS,
  START_WAIT_MS,
  START_POLL_MS,
  DIALOG_SNOOZE_MS,
  classifyProbeError,
  pruneStarts,
  canAutoStart,
  nextStatusAfterTimeout,
  serverInfoFromVersion,
  dialogTexts,
} from '@/services/kkm-server/kkm-watchdog-core'

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Вочдог KkmServer. Спека: docs/superpowers/specs/2026-10-23-kkm-watchdog-design.md
 * (репо fakt-crm-cloude). Мозги здесь, руки - в Electron-оболочке crm-desktop-app-electron
 * (window.faktDesktop.kkm: start/restart/status через задачи Планировщика). Без оболочки
 * не стартует: в браузере, на macOS и Linux поведение CRM не меняется.
 *
 * Раз в 30 с проба GET /VersionJSON по адресу из настроек: штатный маршрут KkmServer без
 * авторизации, команды, очереди устройств и журнала; она же даёт версию для телеметрии.
 * (List шёл через /Execute с preflight и попадал в журнал при отладочном флаге, GetServerData
 * читает лицензию и на кассе идёт дольше 5 с.) Нет связи - автозапуск (не больше трёх за
 * 10 минут) и ожидание до 20 с; не ожил - глобальный confirm-диалог. Два таймаута подряд -
 * «не отвечает», автоматически ничего, только диалог с кнопкой перезапуска.
 */
class KkmWatchdog {
  constructor () {
    this._timer = null
    this._ticking = false
    this._status = STATUS.Unknown
    this._timeoutsInRow = 0
    this._startTimes = []
    this._info = null
    this._dialogTitle = null
    this._dialogSnoozedUntil = 0
  }

  static isAvailable () {
    return Boolean(window.faktDesktop && window.faktDesktop.kkm)
  }

  get bridge () {
    return window.faktDesktop.kkm
  }

  start () {
    if (this._timer || !KkmWatchdog.isAvailable()) return
    console.info('KkmWatchdog: старт, оболочка', window.faktDesktop.appVersion)
    this._timer = setInterval(() => this.tick(), PROBE_INTERVAL_MS)
    this.tick()
  }

  // Внеочередной тик: прокси перед Execute не достучался до KkmServer. Идущий тик не дублируем
  kick () {
    if (this._timer) this.tick()
  }

  async tick () {
    if (this._ticking) return
    this._ticking = true
    try {
      await this._check()
    } catch (e) {
      console.warn('KkmWatchdog: тик упал', e)
    } finally {
      this._ticking = false
    }
  }

  // Проба живости и версия одним запросом. Ответ любого HTTP-кода = жив (classifyProbeError)
  async _probe () {
    const version = await kkmServerApiClient.getVersion(PROBE_TIMEOUT_MS)
    return serverInfoFromVersion(version, Date.now())
  }

  async _check () {
    let info
    try {
      info = await this._probe()
    } catch (e) {
      const kind = classifyProbeError(e)
      if (kind === 'alive') {
        this._alive(null)
        return
      }
      if (kind === 'timeout') {
        this._timeoutsInRow += 1
        if (nextStatusAfterTimeout(this._timeoutsInRow) === STATUS.Hung) {
          this._setStatus(STATUS.Hung)
          this._showDialog()
        }
        return
      }
      this._timeoutsInRow = 0
      await this._down()
      return
    }
    this._alive(info)
  }

  // info null - сервер ответил ошибкой HTTP, версию не узнали, но он жив
  _alive (info) {
    this._timeoutsInRow = 0
    if (info) this._info = info
    this._setStatus(STATUS.Ok)
    this._hideDialog()
  }

  async _down () {
    this._setStatus(STATUS.Down)
    const now = Date.now()
    this._startTimes = pruneStarts(this._startTimes, now)
    if (canAutoStart(this._startTimes, now)) {
      this._startTimes.push(now)
      if (await this._runAndWait(() => this.bridge.start(), 'start')) return
    } else {
      console.warn('KkmWatchdog: лимит автозапусков исчерпан, только диалог')
    }
    this._showDialog()
  }

  // Команда оболочке и ожидание ответа KkmServer; true - ожил
  async _runAndWait (run, name) {
    let result
    try {
      result = await run()
    } catch (e) {
      result = { ok: false, error: String(e) }
    }
    console.info('KkmWatchdog: ' + name, result)
    if (!result || !result.ok) return false
    this._setStatus(STATUS.Starting)
    const deadline = Date.now() + START_WAIT_MS
    while (Date.now() < deadline) {
      await sleep(START_POLL_MS)
      try {
        this._alive(await this._probe())
        return true
      } catch (e) {
        if (classifyProbeError(e) === 'alive') {
          this._alive(null)
          return true
        }
      }
    }
    return false
  }

  _setStatus (status) {
    this._status = status
    store.commit('printServer/setKkmServerInfo', { ...(this._info || {}), status })
  }

  _showDialog () {
    if (this._dialogTitle || Date.now() < this._dialogSnoozedUntil) return
    // глобальный диалог занят другим окном - попробуем на следующем тике
    if (store.state.confirmDialog.show) return
    const texts = dialogTexts(this._status)
    this._dialogTitle = texts.title
    store.dispatch('confirmDialog/show', {
      title: texts.title,
      content: texts.content,
      confirmText: 'Проверить',
      cancelText: 'Закрыть',
      // коротко: KkmServer уже в заголовке, а третья кнопка ряда всего 200px
      extraText: 'Перезапуск',
      extraBtnColor: 'error',
      extraIcon: 'mdi-restart',
      onConfirm: () => {
        this._dialogTitle = null
        this.tick()
      },
      onExtra: () => {
        this._dialogTitle = null
        this._manualRestart()
      },
      onCancel: () => {
        this._dialogTitle = null
        this._dialogSnoozedUntil = Date.now() + DIALOG_SNOOZE_MS
      },
    })
  }

  // Закрываем только свой диалог: другой код мог показать своё окно поверх
  _hideDialog () {
    if (!this._dialogTitle) return
    if (store.state.confirmDialog.show && store.state.confirmDialog.title === this._dialogTitle) {
      store.commit('confirmDialog/hideDialog')
    }
    this._dialogTitle = null
  }

  async _manualRestart () {
    if (this._ticking) return
    this._ticking = true
    try {
      if (!(await this._runAndWait(() => this.bridge.restart(), 'restart'))) this._showDialog()
    } finally {
      this._ticking = false
    }
  }
}

export const kkmWatchdog = new KkmWatchdog()
export default kkmWatchdog
